/**
 * Procedural idle + proportion morphs on top of the procedural rig. The idle is layered sine motion with phase lags
 * (breathing, a slow weight shift, head looks with eased holds, arm sway, a tail wave travelling root → tip, ear
 * twitches); morphs are bone deformation scales, so they compose with any animation.
 */
import type { Skeleton, V3 } from './rig';
import { boneTip, evaluate, qAxisAngle, qEuler, qMul, type Pose, type Quat } from './pose';
import type { EquipmentWeaponId } from '../../shared/equipment';
import { applyArmedMotion, type WeaponHead } from './armedMotion';
import {weaponMotion,isSuperAction,type SuperAction} from '../../shared/weapon-motion';
import {isOriginalSkill} from '../../shared/fable-motion/skills';
export { weaponMotion } from './armedMotion';

import type { Morphs } from '../../shared/atelier';
export type { Morphs } from '../../shared/atelier';
export const DEFAULT_MORPHS: Morphs = { head: 1, belly: 1, legs: 1, arms: 1, tail: 1, ears: 1 };

const smooth = (x: number) => x * x * (3 - 2 * x);
/** A slow "look" target that holds, then eases to a new random-ish value (deterministic in t). */
function lookCurve(t: number, period: number, seed: number): number {
  const k = Math.floor(t / period), f = (t / period) - k;
  const rnd = (n: number) => { const x = Math.sin((n + seed) * 12.9898) * 43758.5453; return x - Math.floor(x); }; // 0..1 (JS % keeps the sign)
  const a = rnd(k), b = rnd(k + 1);
  return a + (b - a) * smooth(Math.min(1, f * 2.5));
}

/** Face-driven extras: talking nods and the emotion's head tilt. Returns the idle look (for the eyes' gaze). */
export type PreviewAction = 'idle' | 'walk' | 'greet' | 'strike' | 'guard' | 'prayer' | SuperAction;
export const PREVIEW_PERIODS = { walk: 2.4, greet: 3.6, strike: 2.8, guard: 3.6, prayer: 3.6, starfall: weaponMotion('hammer','starfall').duration+.8, thousandCuts: weaponMotion('sword','thousandCuts').duration+.8, solarCleave:5.05, seismicSlam:5.85, tempest:5.65, shadowDance:5.15 } as const;
export interface PoseExtra { nod: number; tilt: number; talking: boolean; action?: PreviewAction; weapon?: EquipmentWeaponId; weaponHead?:WeaponHead; oneShot?:boolean }
const aim = (from:number[],to:number[]):Quat => {
  const a=Math.hypot(...from)||1,b=Math.hypot(...to)||1,f=from.map(v=>v/a),t=to.map(v=>v/b);
  const q=[f[1]*t[2]-f[2]*t[1],f[2]*t[0]-f[0]*t[2],f[0]*t[1]-f[1]*t[0],1+f.reduce((sum,v,i)=>sum+v*t[i],0)];
  const length=Math.hypot(...q)||1;return q.map(v=>v/length) as Quat;
};
/** The same anatomical guard and prayer used by fitting and combat. */
function raisedHands(sk:Skeleton,pose:Pose,prayer:boolean):void {
  if (prayer) { prayerHands(sk, pose); return; }
  for(const [side,sign] of [['R',-1],['L',1]] as const) {
    const upper=sk.byName.get('upperArm'+side),fore=sk.byName.get('foreArm'+side);if(upper===undefined||fore===undefined)continue;
    const ub=sk.bones[upper],fb=sk.bones[fore],u=aim(ub.tail.map((v,i)=>v-ub.head[i]),[sign*.12,-.79,.6]);
    const target=[-sign*(prayer?.65:.25),.85,prayer?.25:.44];
    const inv:Quat=[-u[0],-u[1],-u[2],u[3]],local=qMul(qMul(inv,[target[0],target[1],target[2],0]),u);
    pose.bones[upper].rot=u;pose.bones[fore].rot=aim(fb.tail.map((v,i)=>v-fb.head[i]),local.slice(0,3));
  }
  for(const [name,angle] of [['chest',-.04],['head',prayer?-.15:0]] as const){const i=sk.byName.get(name);if(i!==undefined)pose.bones[i].rot=qMul(pose.bones[i].rot,qEuler(angle,0,0));}
}
/** Solve the two arm segments toward separate palms in chest space. A fixed
 * inward forearm angle crosses the long Inky wrists and cannot join Hare's
 * shorter arms. The target remains reachable when arm/belly proportions vary. */
function prayerHands(sk: Skeleton, pose: Pose): void {
  const chest = sk.byName.get('chest'), neck = sk.byName.get('neck');
  if (chest === undefined || neck === undefined) return;
  pose.bones[chest].rot = qMul(pose.bones[chest].rot, qEuler(-.04, 0, 0));
  const head = sk.byName.get('head');
  if (head !== undefined) pose.bones[head].rot = qMul(pose.bones[head].rot, qEuler(-.15, 0, 0));
  const ev = evaluate(sk, pose), frame = ev.world[chest];
  const local = (p: V3): V3 => {
    const d = p.map((v, i) => v - frame[9 + i]);
    return [0, 1, 2].map(i => frame[i * 3] * d[0] + frame[i * 3 + 1] * d[1] + frame[i * 3 + 2] * d[2]) as V3;
  };
  const dot = (a: number[], b: number[]) => a.reduce((sum, v, i) => sum + v * b[i], 0);
  const norm = (v: number[]): V3 => { const length = Math.hypot(...v) || 1; return v.map(x => x / length) as V3; };
  const inverse = (q: Quat): Quat => [-q[0], -q[1], -q[2], q[3]];
  const rotate = (q: Quat, v: number[]): V3 => qMul(qMul(q, [...v, 0] as Quat), inverse(q)).slice(0, 3) as V3;
  const neckPoint = local(ev.joints[neck]);
  const chin = head === undefined ? Infinity : local(ev.joints[head])[1] - sk.bones[head].radius * pose.bones[head].length;
  const pelvis = sk.byName.get('pelvis');
  // Raised wrists may sit at belly height on large-headed, short-legged bodies.
  // Reserve clearance for the whole torso, not only the narrower upper chest.
  const torsoRadius = Math.max(sk.bones[chest].radius * pose.bones[chest].thick,
    pelvis === undefined ? 0 : sk.bones[pelvis].radius * pose.bones[pelvis].thick);
  for (const [side, sign] of [['R', -1], ['L', 1]] as const) {
    const upper = sk.byName.get('upperArm' + side), fore = sk.byName.get('foreArm' + side), hand = sk.byName.get('hand' + side);
    if (upper === undefined || fore === undefined || hand === undefined) continue;
    const hb = sk.bones[hand];
    const front = torsoRadius + hb.radius + .015;
    const direction = (i: number): V3 => sk.bones[i].tail.map((v, axis) => v - sk.bones[i].head[axis]) as V3;
    const upperLength = Math.hypot(...direction(upper)) * pose.bones[upper].length;
    const foreLength = Math.hypot(...direction(fore)) * pose.bones[fore].length;
    const handLength = Math.hypot(...direction(hand));
    const shoulder = local(ev.joints[upper]);
    const maximum = (upperLength + foreLength) * .97;
    // Preserve forward clearance first. Short arms on a broad torso may not
    // reach the centre; keep the palms slightly apart instead of burying both
    // wrists in the chest by clamping the entire shoulder→target vector.
    const dz = Math.min(front - shoulder[2], maximum * .88);
    const desiredY = Math.min(shoulder[1] - .02, neckPoint[1] - handLength * .9, chin - handLength * .65 - .022);
    const dy = Math.max(-Math.sqrt(maximum * maximum - dz * dz) * .78, desiredY - shoulder[1]);
    const inward = Math.sqrt(Math.max(0, maximum * maximum - dz * dz - dy * dy));
    const goal: V3 = [sign * Math.max(hb.radius * .95, sign * shoulder[0] - inward), shoulder[1] + dy, shoulder[2] + dz];
    const delta = goal.map((v, i) => v - shoulder[i]);
    const reach = Math.max(Math.abs(upperLength - foreLength) + .001, Math.min(Math.hypot(...delta), (upperLength + foreLength) * .97));
    const axis = norm(delta), wrist = shoulder.map((v, i) => v + axis[i] * reach) as V3;
    const along = (upperLength * upperLength - foreLength * foreLength + reach * reach) / (2 * reach);
    const bend = Math.sqrt(Math.max(0, upperLength * upperLength - along * along));
    // Slender, long-armed figures can let their elbows hang naturally. Blend
    // forward/outward only as torso thickness consumes the available reach;
    // a downward pole otherwise folds the forearm through a broad belly.
    const clearance = smooth(Math.max(0, Math.min(1, (torsoRadius / (upperLength + foreLength) - .35) / .2)));
    const pole = [sign * (.35 + .65 * clearance), -1 + .5 * clearance, .3 + 1.2 * clearance], projection = dot(pole, axis);
    const outward = norm(pole.map((v, i) => v - axis[i] * projection));
    const elbow = shoulder.map((v, i) => v + axis[i] * along + outward[i] * bend);
    const upperRot = aim(direction(upper), elbow.map((v, i) => v - shoulder[i]));
    const foreRot = aim(direction(fore), rotate(inverse(upperRot), wrist.map((v, i) => v - elbow[i])));
    pose.bones[upper].rot = upperRot; pose.bones[fore].rot = foreRot;
    // Fingers point up; turn the source palm plane toward its opposite palm,
    // rather than presenting two spread palms to the viewer.
    const fingerUp = Math.max(.15, Math.min(.998, (chin - wrist[1] - .022) / handLength));
    const fingers: V3 = [0, fingerUp, Math.sqrt(1 - fingerUp * fingerUp)], handAim = aim(direction(hand), fingers);
    const normal = rotate(handAim, [0, 0, 1]);
    const palmNormal = norm(normal.map((v, i) => v - fingers[i] * dot(normal, fingers)));
    const facing = [-sign, 0, 0];
    const cross = [palmNormal[1] * facing[2] - palmNormal[2] * facing[1], palmNormal[2] * facing[0] - palmNormal[0] * facing[2], palmNormal[0] * facing[1] - palmNormal[1] * facing[0]];
    const twist = Math.atan2(dot(fingers, cross), dot(palmNormal, facing));
    const handRot = qMul(qAxisAngle(...fingers, twist), handAim);
    pose.bones[hand].rot = qMul(inverse(qMul(upperRot, foreRot)), handRot);
  }
}
/** Keep the lower planted foot at its rest height after all proportions and rotations. */
function groundPose(sk: Skeleton, pose: Pose): void {
  const feet = ['footR', 'footL'].map(name => sk.byName.get(name)).filter((i): i is number => i !== undefined);
  if (!feet.length) return;
  const ev = evaluate(sk, pose);
  const delta = Math.min(...feet.map(i => boneTip(sk, pose, ev, i)[1] - sk.bones[i].tail[1]));
  pose.rootOffset[1] -= delta;
}
/** Long curled tails can extend below the planted feet on short-legged morphs.
 * Lift the tail around its own root; never levitate the character to hide it. */
function clearTailFloor(sk:Skeleton,pose:Pose):void {
  const root=sk.byName.get('tail0');if(root===undefined)return;
  const chain=sk.bones.map((b,i)=>b.chain==='tail'?i:-1).filter(i=>i>=0);
  for(let pass=0;pass<2;pass++){
    const ev=evaluate(sk,pose),pivot=ev.joints[root];let bottom=Infinity,low:V3=pivot;
    for(const i of chain)for(const p of [ev.joints[i],boneTip(sk,pose,ev,i)]){const y=p[1]-sk.bones[i].radius*pose.bones[i].thick;if(y<bottom){bottom=y;low=p;}}
    if(bottom>=.008)return;
    const dx=low[0]-pivot[0],dz=low[2]-pivot[2],distance=Math.hypot(dx,dz);if(distance<.02)return;
    const axis:V3=[-dz/distance,0,dx/distance],parent=ev.world[sk.bones[root].parent];
    const local=[0,1,2].map(i=>parent[i*3]*axis[0]+parent[i*3+1]*axis[1]+parent[i*3+2]*axis[2]) as V3;
    pose.bones[root].rot=qMul(qAxisAngle(...local,Math.min(.55,Math.atan2(.012-bottom,distance))),pose.bones[root].rot);
  }
}

/** Blend from a captured pose into the freshly evaluated target. The snapshot
 * stays immutable; quaternion hemisphere correction takes the shortest turn. */
export function blendPreviewPose(sk: Skeleton, target: Pose, from: Pose, weight: number): void {
  const t = Math.max(0, Math.min(1, weight));
  if (target.bones.length !== from.bones.length || target.bones.length !== sk.bones.length) return;
  for (let i = 0; i < target.bones.length; i++) {
    const a = from.bones[i], b = target.bones[i];
    const sign = a.rot.reduce((sum, v, k) => sum + v * b.rot[k], 0) < 0 ? -1 : 1;
    const rot = a.rot.map((v, k) => v * (1 - t) + b.rot[k] * sign * t), norm = Math.hypot(...rot) || 1;
    b.rot = rot.map(v => v / norm) as typeof b.rot;
    b.length = a.length * (1 - t) + b.length * t;
    b.thick = a.thick * (1 - t) + b.thick * t;
    if(a.offset||b.offset)b.offset=[0,1,2].map(k=>(a.offset?.[k]??0)*(1-t)+(b.offset?.[k]??0)*t) as V3;
  }
  target.rootOffset = target.rootOffset.map((v, k) => from.rootOffset[k] * (1 - t) + v * t) as typeof target.rootOffset;
  groundPose(sk, target);
}
export function applyPose(sk: Skeleton, pose: Pose, t: number, m: Morphs, idle: boolean, extra: PoseExtra = { nod: 0, tilt: 0, talking: false }): { yaw: number; pitch: number } {
  const bi = (n: string) => sk.byName.get(n) ?? -1;
  for (const b of pose.bones) { b.rot = [0, 0, 0, 1]; b.length = 1; b.thick = 1; delete b.offset; }
  pose.rootOffset = [0, 0, 0];
  const set = (n: string, x: number, y: number, z: number) => { const i = bi(n); if (i >= 0) pose.bones[i].rot = qMul(pose.bones[i].rot, qEuler(x, y, z)); };
  const scale = (n: string, length: number, thick: number) => { const i = bi(n); if (i >= 0) { pose.bones[i].length *= length; pose.bones[i].thick *= thick; } };
  const armAngle = (name: string) => {
    const i = bi(name); if (i < 0) return 0;
    const { head, tail } = sk.bones[i];
    return Math.atan2(Math.abs(tail[0] - head[0]), Math.max(.001, head[1] - tail[1]));
  };
  const relaxedArms = (motionTime: number | null) => {
    for (const [side, sign] of [['R', -1], ['L', 1]] as const) {
      const sway = motionTime === null ? 0 : Math.sin(motionTime * 1.05 + (sign > 0 ? .8 : 0));
      const upper = Math.max(0, Math.min(.52, armAngle(`upperArm${side}`) - .28));
      const lower = Math.max(0, Math.min(.16, armAngle(`foreArm${side}`) - upper - .3));
      set(`upperArm${side}`, .02 + sway * .04, 0, -sign * upper + sway * .012);
      set(`foreArm${side}`, -.1 + sway * .04, 0, -sign * lower);
      set(`hand${side}`, sway * .045, 0, sign * .018);
    }
  };

  // ── morphs ──
  scale('head', m.head, m.head);
  // Bone scales are deliberately not inherited; ears must explicitly share
  // head size or an enlarged skull moves their roots while leaving tiny tips.
  for (const b of sk.bones) if (b.chain === 'ear') scale(b.name, m.ears * m.head, m.ears * m.head);
  scale('pelvis', 1 + (m.belly - 1) * 0.3, m.belly); scale('chest', 1, 1 + (m.belly - 1) * 0.55);
  for (const s of ['R', 'L']) {
    scale(`thigh${s}`, m.legs, 1 + (m.belly - 1) * 0.25); scale(`shin${s}`, m.legs, 1);
    scale(`upperArm${s}`, m.arms, 1); scale(`foreArm${s}`, m.arms, 1);
  }
  for (let i = 0; bi(`tail${i}`) >= 0; i++) scale(`tail${i}`, m.tail, 1);
  if (!idle) { relaxedArms(null); set('head', extra.nod * 0.12, 0, extra.tilt); if(extra.weapon)applyArmedMotion(sk,pose,extra.action==='guard'?.18:0,extra.weapon,extra.action==='guard'?'guard':'idle',extra.weaponHead);groundPose(sk, pose); return { yaw: 0, pitch: 0 }; }

  const action = extra.action ?? 'idle';
  if(isSuperAction(action)||action==='strike'&&extra.weapon) {
    relaxedArms(null);
    const clock=extra.oneShot?Math.max(0,t):((t%PREVIEW_PERIODS[action])+PREVIEW_PERIODS[action])%PREVIEW_PERIODS[action];
    const hover=applyArmedMotion(sk,pose,clock,extra.weapon??(action==='starfall'?'hammer':'sword'),action,extra.weaponHead);
    set('head',extra.nod*.12,0,extra.tilt);groundPose(sk,pose);pose.rootOffset[1]+=hover;if(isOriginalSkill(action))clearTailFloor(sk,pose);
    return {yaw:0,pitch:0};
  }
  if(action==='guard'||action==='prayer'){
    const look=applyPose(sk,pose,t,m,true,{...extra,action:'idle'});
    raisedHands(sk,pose,action==='prayer');
    if(extra.weapon)applyArmedMotion(sk,pose,t,extra.weapon,action,extra.weaponHead);
      groundPose(sk,pose);
    return look;
  }
  if (action !== 'idle') {
    const period = PREVIEW_PERIODS[action], cycle = ((t % period) + period) % period / period;
    const phase = cycle * Math.PI * 2;
    const ease = (x: number) => smooth(Math.max(0, Math.min(1, x)));
    let yaw = 0, pitch = 0;
    if (action === 'walk') {
      const stride = Math.sin(phase);
      relaxedArms(null);
      set('pelvis', 0, stride * 0.035, stride * 0.025);
      set('chest', 0.02, -stride * 0.04, -stride * 0.025);
      for (const [side, sign] of [['R', 1], ['L', -1]] as const) {
        const step = stride * sign, thigh = -step * 0.31, knee = Math.max(0, -step) * 0.46;
        set(`thigh${side}`, thigh, 0, 0); set(`shin${side}`, knee, 0, 0);
        set(`foot${side}`, -(thigh + knee) * 0.75, 0, 0);
        set(`upperArm${side}`, step * 0.28, 0, 0);
        set(`foreArm${side}`, -.08 - Math.max(0, step) * .08, 0, 0);
        const hand = bi(`hand${side}`);
        if (hand >= 0) {
          const b = sk.bones[hand], direction = b.tail.map((v, i) => v - b.head[i]) as V3;
          // Keep the relaxed fingers, with palms turned toward the body rather
          // than the source A-pose's permanent open-handed shrug.
          pose.bones[hand].rot = qMul(pose.bones[hand].rot, qAxisAngle(...direction, -sign * 1.05));
        }
      }
      pitch = Math.cos(phase * 2) * 0.02;
    } else if (action === 'greet') {
      const raised = ease(cycle / 0.2) * (1 - ease((cycle - 0.73) / 0.24));
      relaxedArms(null);
      const restAngle = armAngle('upperArmR'), relaxed = Math.max(0, Math.min(.52, restAngle - .28));
      const upperLift = 1.78 - restAngle + relaxed;
      set('upperArmR', -.1 * raised, 0, -upperLift * raised);
      set('foreArmR', -.12 * raised, 0, -.85 * raised);
      set('handR', 0, .1 * raised, Math.sin(phase * 4) * .38 * raised);
      set('chest', 0, -0.04 * raised, 0.025 * raised);
      yaw = -0.1 * raised;
    } else {
      const wind = ease(cycle / 0.25) * (1 - ease((cycle - 0.25) / 0.2));
      const hit = ease((cycle - 0.28) / 0.13) * (1 - ease((cycle - 0.52) / 0.35));
      set('pelvis', 0, -0.12 * wind + 0.2 * hit, 0);
      set('chest', 0.14 * hit, -0.18 * wind + 0.16 * hit, -0.035 * hit);
      set('upperArmR', 0.6 * wind - 1.05 * hit, 0, -0.35 * hit);
      set('foreArmR', -0.8 * wind - 0.15 * hit, 0, -0.12 * hit);
      set('upperArmL', -0.25 * hit, 0, 0.12 * hit); set('foreArmL', -0.25, 0, 0);
      for (const side of ['R', 'L']) { set(`thigh${side}`, -0.07 * hit, 0, 0); set(`shin${side}`, 0.15 * hit, 0, 0); }
      yaw = 0.08 * wind - 0.1 * hit; pitch = -0.08 * hit;
    }
    set('head', pitch + extra.nod * 0.12, yaw, extra.tilt);
    const tails = sk.bones.filter(b => b.chain === 'tail');
    tails.forEach((bone, i) => {
      const progress = i / Math.max(1, tails.length - 1), amplitude = (0.015 + progress * 0.06) * Math.min(1, 6 / tails.length);
      set(bone.name, 0, Math.sin(phase - progress * 2) * amplitude, Math.sin(phase - progress * 3) * amplitude);
    });
    if(extra.weapon)applyArmedMotion(sk,pose,t,extra.weapon,action,extra.weaponHead);
    groundPose(sk, pose);
    return { yaw, pitch };
  }

  // ── idle ──
  const breath = Math.sin(t * 2.1);
  scale('chest', 1 + breath * 0.012, 1 + breath * 0.022);
  pose.rootOffset[1] += -0.003 + breath * 0.002;
  const shift = Math.sin(t * 0.55);
  pose.rootOffset[0] += shift * 0.006;
  set('pelvis', 0, shift * 0.04, -shift * 0.025);
  set('chest', 0.02 + breath * 0.01, -shift * 0.05, shift * 0.03);
  // knees soften against the weight shift
  set('thighR', -0.04 - Math.max(0, shift) * 0.05, 0, 0); set('shinR', 0.06 + Math.max(0, shift) * 0.08, 0, 0);
  set('thighL', -0.04 - Math.max(0, -shift) * 0.05, 0, 0); set('shinL', 0.06 + Math.max(0, -shift) * 0.08, 0, 0);
  // head: eased looks with holds, plus a tilt
  // while talking the head faces the viewer and nods on stressed vowels
  const k = extra.talking ? 0.3 : 1;
  const yaw = (lookCurve(t, 3.1, 1.7) - 0.5) * 0.6 * k;
  const pitch = (lookCurve(t, 4.3, 5.2) - 0.5) * 0.3 * k;
  set('neck', pitch * 0.4, yaw * 0.35, 0);
  set('head', pitch * 0.6 + extra.nod * 0.12, yaw * 0.65, Math.sin(t * 0.7) * 0.08 * k + extra.tilt);
  // Lower each source model's A-pose to a relaxed arm angle; the narrow Inky
  // shoulders need much less correction than the broad Round or Hare bases.
  relaxedArms(t);
  // tail: travelling wave, amplitude grows toward the tip
  // (the root barely moves — rotating it swings the whole tail like a flipper; motion builds toward the tip)
  const tailCount = sk.bones.filter(b => b.chain === 'tail').length;
  for (let i = 0; i < tailCount; i++) {
    const progress = i / Math.max(1, tailCount - 1);
    const a = (0.025 + progress * 0.15) * Math.min(1, 6 / tailCount);
    set(`tail${i}`, Math.sin(t * 1.3 - progress * 3.5) * a * 0.6, Math.sin(t * 1.05 - progress * 3) * a * 0.5, Math.sin(t * 1.5 - progress * 4) * a);
  }
  // ear twitch every few seconds
  const tw = Math.max(0, Math.sin(t * 0.9) - 0.93) * 12;
  set('earL', 0, 0, -tw * 0.35); set('earR', 0, 0, Math.max(0, Math.sin(t * 0.9 + 2) - 0.95) * 12 * 0.35);
  if(extra.weapon)applyArmedMotion(sk,pose,t,extra.weapon,'idle',extra.weaponHead);
  groundPose(sk, pose);
  return { yaw, pitch };
}
